import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from 'react-map-gl';

import { currentLocale } from '../components/numericFormat';
import { ParcelHoverSource, parcelHoverText } from './parcelHover';

/**
 * The parcel under the pointer, named.
 *
 * A hook and a component rather than a copy in each map, because the three maps of the dashboard
 * draw the same parcels and a reader moving between them should not have to learn two layouts.
 *
 * The hook holds the position and the parcel id; what is *known* about that id stays with the map,
 * which is the only one that knows whether the id came from the plot or from the neighbourhood.
 */

export interface ParcelHoverState {
    parcelId: string;
    longitude: number;
    latitude: number;
}

export const useParcelHover = () => {
    const [hovered, setHovered] = useState<ParcelHoverState | null>(null);

    /**
     * Reads the topmost feature under the pointer.
     *
     * Topmost is the right one: the layers are drawn neighbours-first and plot-last, so where two
     * outlines overlap the one the reader sees is the one they are asking about.
     */
    const onMouseMove = useCallback((event: any) => {
        const feature = event?.features?.[0];
        const parcelId = feature?.properties?.parcelId;

        if (!parcelId) {
            setHovered(null);
            return;
        }

        setHovered({
            parcelId: String(parcelId),
            longitude: event.lngLat.lng,
            latitude: event.lngLat.lat,
        });
    }, []);

    // Leaving the canvas has to clear it: the popup is placed in map coordinates and would otherwise
    // hang on the last parcel while the pointer is elsewhere entirely.
    const onMouseLeave = useCallback(() => setHovered(null), []);

    return { hovered, onMouseMove, onMouseLeave, clearHover: onMouseLeave };
};

export default function ParcelTooltip({
    hovered,
    parcel,
}: {
    hovered: ParcelHoverState | null;
    /** What is known about the hovered parcel; absent while nothing is known about it. */
    parcel: ParcelHoverSource | null;
}) {
    const { t } = useTranslation();

    if (!hovered || !parcel) return <></>;

    const text = parcelHoverText(
        parcel,
        (value) => new Intl.NumberFormat(currentLocale()).format(value),
        t('portfolioDashboard.parcels.number'),
        t('portfolioDashboard.parcels.district'),
        t('portfolioDashboard.parcels.section'),
        // Ohne Nachkommastellen: Ein Bodenrichtwert wird in ganzen Euro je Quadratmeter geführt,
        // und „412,00 €/m²" behauptet eine Genauigkeit, die die Quelle nicht hat.
        (value) => new Intl.NumberFormat(currentLocale(), { maximumFractionDigits: 0 }).format(value),
    );

    return (
        <Popup
            longitude={hovered.longitude}
            latitude={hovered.latitude}
            closeButton={false}
            closeOnClick={false}
            // No fixed anchor: the small map beside the property is 170 px tall, and a bubble that
            // always hangs below the pointer is clipped by its own map. Mapbox picks the side that
            // fits. That it does not steal the pointer's next event is handled in the stylesheet,
            // where `.parcelTooltip` sets `pointer-events: none` — without that, a bubble under the
            // cursor makes the hover flicker off and on.
            offset={12}
            maxWidth="280px"
            className="parcelTooltip"
        >
            <div className="flex flex-col gap-0.5 text-xs">
                <span className="font-semibold">{text.title}</span>
                {text.place ? <span className="text-muted-foreground">{text.place}</span> : <></>}
                {text.area ? <span className="tabular-nums">{text.area}</span> : <></>}
                {/* Der Bodenrichtwert direkt hinter der Fläche: Beides sind Maße desselben Stücks
                    Boden, und zusammen ergeben sie erst die Frage, um die es geht. */}
                {text.landValue ? (
                    <span className="tabular-nums font-medium">{text.landValue}</span>
                ) : (
                    <></>
                )}
                {text.roleKey ? <span>{t(text.roleKey)}</span> : <></>}
                {text.reasons.map((reason) => (
                    <span key={reason} className="text-muted-foreground">
                        {reason}
                    </span>
                ))}
            </div>
        </Popup>
    );
}
